<template>
	<o-tour :ref="setup._tourOrderPipeline">
		<o-tour-step
			target="slider"
			size="md"
			:title="$t('business.settings.agency.contract.validationSteps')"
			hide-finish>
			<p>
				{{ $t('business.settings.agency.contract.pipelineGlobalView') }}
			</p>
			<p>
				{{ $t('business.settings.agency.contract.dragDropCandidates') }}
			</p>
			<p>
				{{ $t('business.settings.agency.contract.multiselectionAction') }}
			</p>
			<p>
				{{ $t('business.settings.agency.contract.noImposedOrder') }}
			</p>

			<template #actions="{ next }">
				<o-button
					size="xs"
					outline
					@click="setup.returnToTourOrderDetails()">
					{{ $t('common.actions.cancel') }}
				</o-button>
				<o-button
					size="xs"
					color="brand"
					@click="next">
					{{ $t('common.actions.validate') }}
				</o-button>
			</template>
		</o-tour-step>

		<o-tour-step
			:target="setup.scrollToId.bind(setup, 'step-0')"
			:title="$t('business.settings.agency.contract.presumedCandidate')"
			hide-finish>
			<p v-html="$t('business.tour.pipeline.step1.description')"/>
			<p>
				{{ $t('business.settings.agency.contract.whenClickIcon') }}
				<o-icon
					style="margin-right: 0.25rem; margin-left: 0.25rem;"
					font-icon="icon-interface-add-circle"
					color="info"/>
				<span v-html="$t('business.tour.pipeline.step1.searchWindow')"/>
			</p>
			<p v-html="$t('business.tour.pipeline.step1.candidatesInfo')"/>
		</o-tour-step>

		<o-tour-step
			:target="setup.scrollToId.bind(setup, 'step-1')"
			:title="$t('business.settings.agency.contract.proposedToCandidate')"
			size="lg"
			hide-finish>
			<p>
				{{ $t('business.settings.agency.contract.proposeMission') }}
			</p>
			<p v-html="$t('business.tour.pipeline.step2.autoEmail')"/>
			<o-alert color="info">
				<span v-html="$t('business.tour.pipeline.step2.missionDescription')"/>
				<br>
				<span v-html="$t('business.tour.pipeline.step2.saveTime')"/>
			</o-alert>
			<p v-html="$t('business.tour.pipeline.step2.candidatePosition')"/>
			<p class="flex ai-c g-xs">
				{{ $t('business.settings.agency.contract.whileNotResponded') }}
				<shared-state-icon
					small
					:state="'warning'"/>
			</p>
			<div>
				<span v-html="$t('business.tour.pipeline.step2.responseColor')"/>
				<br>
				<div class="flex ai-c g-xs">
					<shared-state-icon
						small
						:state="'complete'"/><span
							style="font-weight: bold;"
							class="text--success">
							{{ $t('business.tour.pipeline.step2.offerValidation') }}
						</span>
				</div>
				<div class="flex ai-c g-xs">
					<shared-state-icon
						small
						:state="'danger'"/><span
							style="font-weight: bold;"
							class="text--danger">
							{{ $t('business.tour.pipeline.step2.offerRefusal') }}
						</span>.
				</div>
			</div>
		</o-tour-step>

		<o-tour-step
			:target="setup.scrollToId.bind(setup, 'step-2')"
			size="lg"
			:title="$t('business.proposedToClient')"
			hide-finish>
			<div>
				<span v-html="$t('business.tour.pipeline.step3.dropCandidate')"/>
				<br>
				<div class="flex ai-c g-xs">
					{{ $t('business.tour.pipeline.step3.iconAppears') }}
					<shared-state-icon
						small
						:state="'warning'"/>
				</div>
			</div>
			<p>
				<span v-html="$t('business.tour.pipeline.step3.clientNotified')"/>
				<br>
				<span v-html="$t('business.tour.pipeline.step3.youNotified')"/>
				<br>
				{{ $t('business.tour.pipeline.step3.iconColoredByClient') }}
			</p>
			<div>
				<div class="flex ai-c g-xs">
					<shared-state-icon
						small
						:state="'complete'"/><span
							style="font-weight: bold;"
							class="text--success">
							{{ $t('business.tour.pipeline.step3.candidateValidation') }}
						</span>
				</div>
				<div class="flex ai-c g-xs">
					<shared-state-icon
						small
						:state="'danger'"/><span
							style="font-weight: bold;"
							class="text--danger">
							{{ $t('business.tour.pipeline.step3.candidateRefusal') }}
						</span>.
				</div>
			</div>
			<p v-html="$t('business.tour.pipeline.step3.hoverComment')"/>
			<p v-html="$t('business.tour.pipeline.step3.profileAnonymized')"/>
			<o-alert
				v-if="!setup.warnClient"
				color="warning">
				{{ $t('business.tour.pipeline.step3.notSharedWarning') }}
				<br>
				{{ $t('business.tour.pipeline.step3.cannotDrag') }}
				<br>
				{{ $t('business.tour.pipeline.step3.changeYourMind') }}
				<o-icon
					v-tooltip="$t('common.order.notAvailableToShare')"
					style=" margin-right: 0.25rem; margin-left: 0.5rem; font-size: 1.125rem;"
					font-icon="icon-upload-box-2-2"/>
				{{ $t('business.tour.pipeline.step3.iconLocation') }}
			</o-alert>

			<template #actions="{ next, previous }">
				<o-button
					v-if="!setup.warnClient"
					size="xs"
					outline
					color="info"
					@click="setup.updateWarnClient()">
					{{ $t('business.tour.pipeline.step3.shareOrderToClient') }}
				</o-button>
				<o-button
					size="xs"
					outline
					@click="previous">
					{{ $t('common.actions.cancel') }}
				</o-button>
				<o-button
					size="xs"
					color="brand"
					@click="next">
					{{ $t('common.actions.validate') }}
				</o-button>
			</template>
		</o-tour-step>

		<o-tour-step
			:target="setup.scrollToId.bind(setup, 'step-3')"
			:title="$t('common.validated')"
			size="lg">
			<p v-html="$t('business.tour.pipeline.step4.beforeValidating')"/>
			<div>
				{{ $t('business.tour.pipeline.step4.finalValidation') }}
				<ul>
					<li>
						{{ $t('business.tour.pipeline.step4.clientInformed') }}
						<br>
						{{ $t('business.tour.pipeline.step4.clientCanView') }}
					</li>
					<li>
						{{ $t('business.tour.pipeline.step4.candidatesInformed') }}
					</li>
				</ul>
			</div>
			<p v-html="$t('business.tour.pipeline.step4.essentialStep')"/>
			<p v-html="$t('business.tour.pipeline.step4.completionGauge')"/>
			<template #actions="{ previous }">
				<o-button
					size="xs"
					outline
					@click="previous">
					{{ $t('common.actions.cancel') }}
				</o-button>
				<o-button
					size="xs"
					outline
					@click="setup.returnToTourOrderDetails()">
					{{ $t('common.actions.returnToTutorial') }}
				</o-button>
			</template>
		</o-tour-step>
	</o-tour>
</template>

<script setup lang="ts">
// #region Setup
import type { TourOrderPipelineEmits, TourOrderPipelineProps } from './TourOrderPipelineSetup';
import TourOrderPipelineSetup from './TourOrderPipelineSetup';
const emits = defineEmits<TourOrderPipelineEmits>() as TourOrderPipelineEmits;
const props = withDefaults(defineProps<TourOrderPipelineProps>(), TourOrderPipelineSetup.defaultProps);
const setup = new TourOrderPipelineSetup(props, emits);
// #endregion
</script>
